import { Capacitor } from '@capacitor/core';
import { Directory, Filesystem } from '@capacitor/filesystem';
import { Share } from '@capacitor/share';

import { API, getRequestBlob } from '@/api/api';
import { isMediaNotReady } from '@/common/error';
import { toast } from '@/utils/toast';

const blobToBase64 = (blob: Blob) =>
  new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve((reader.result as string).split(',')[1]);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });

const sanitizeFilename = (name: string) => name.replace(/[^a-z0-9._-]/gi, '_');

export const downloadBlob = async (blob: Blob, filename: string, shareTitle?: string) => {
  const platform = Capacitor.getPlatform();

  if (platform === 'web') {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    document.body.appendChild(link);
    link.href = url;
    link.download = filename;
    link.click();
    URL.revokeObjectURL(url);
    document.body.removeChild(link);
    return;
  }

  const data = await blobToBase64(blob);
  const { uri } = await Filesystem.writeFile({
    path: sanitizeFilename(filename),
    data,
    directory: Directory.Cache,
  });
  await Share.share({ title: shareTitle ?? filename, url: uri });
};

/**
 * A video or audio file still being processed is not a failed download, so it is
 * told here, once, and the promise resolves: every caller's "Couldn't download"
 * stays for the real failures.
 */
export const downloadFromLink = async (storageLink: string, filename: string) => {
  let blob: Blob;
  try {
    blob = await getRequestBlob(API.DOCUMENTS, storageLink);
  } catch (err) {
    if (!isMediaNotReady(err)) throw err;
    toast.info('This file isn’t ready. Please try again shortly.', {
      id: 'media-not-ready',
    });
    return;
  }
  await downloadBlob(blob, filename);
};
